import type { SummaryStep } from '~/types/estimates';
import { isPrestationSummaryStep, isRentalSummaryStep } from '~/utils/etaiement-rentals';

export type SummaryStepGroup = {
  title?: string;
  steps: SummaryStep[];
};

const isAssemblyOrDisassemblyStep = (value: string | null | undefined): boolean => (
  value === 'assembly' || value === 'disassembly'
);

const isStudyStep = (value: string | null | undefined): boolean => (
  value === 'study'
);

export const groupSummarySteps = (steps: SummaryStep[]): SummaryStepGroup[] => {
  const positiveSteps = steps.filter((s) => s.total > 0);
  const standardSteps: SummaryStep[] = [];
  const rentalSteps: SummaryStep[] = [];
  const prestationSteps: SummaryStep[] = [];

  positiveSteps.forEach((step) => {
    if (isRentalSummaryStep(step.value)) {
      rentalSteps.push(step);
    } else if (isPrestationSummaryStep(step.value)) {
      prestationSteps.push(step);
    } else {
      standardSteps.push(step);
    }
  });

  const groups: SummaryStepGroup[] = [];

  if (standardSteps.length > 0) {
    groups.push({ steps: standardSteps });
  }
  if (prestationSteps.length > 0) {
    groups.push({ title: 'Prestations', steps: prestationSteps });
  }
  if (rentalSteps.length > 0) {
    groups.push({ title: 'Location', steps: rentalSteps });
  }

  return groups;
};

/** Étaiement: Location, then Montage/Démontage, then Frais d'études. */
export const groupEtaiementSummarySteps = (steps: SummaryStep[]): SummaryStepGroup[] => {
  const positiveSteps = steps.filter((s) => s.total > 0);
  const earlySteps: SummaryStep[] = [];
  const montageSteps: SummaryStep[] = [];
  const studySteps: SummaryStep[] = [];
  const rentalSteps: SummaryStep[] = [];
  const prestationSteps: SummaryStep[] = [];

  positiveSteps.forEach((step) => {
    if (isRentalSummaryStep(step.value)) {
      rentalSteps.push(step);
    } else if (isPrestationSummaryStep(step.value)) {
      prestationSteps.push(step);
    } else if (isAssemblyOrDisassemblyStep(step.value)) {
      montageSteps.push(step);
    } else if (isStudyStep(step.value)) {
      studySteps.push(step);
    } else {
      earlySteps.push(step);
    }
  });

  const groups: SummaryStepGroup[] = [];

  if (earlySteps.length > 0) {
    groups.push({ steps: earlySteps });
  }
  if (prestationSteps.length > 0) {
    groups.push({ title: 'Prestations', steps: prestationSteps });
  }
  if (rentalSteps.length > 0) {
    groups.push({ title: 'Location', steps: rentalSteps });
  }
  if (montageSteps.length > 0) {
    groups.push({ steps: montageSteps });
  }
  if (studySteps.length > 0) {
    groups.push({ steps: studySteps });
  }

  return groups;
};

export const formatSummaryStepQuantityDisplay = (step: SummaryStep): string => {
  const raw = step.quantity?.value;
  if (isRentalSummaryStep(step.value) && Number(raw) === -1) {
    return `1${step.quantity?.unit ? ` ${step.quantity.unit}` : ''}`;
  }
  return `${raw}${step.quantity?.unit ? ` ${step.quantity.unit}` : ''}`;
};
